import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import useFetchSelector from '@lootboy/lib/hooks/useFetchSelector';
import { userSelector } from '@lootboy/lib/selectors';
import { cardsSelector, fetchCardsIfNeeded } from '@lootboy/lib/slices/cards';

import useIsSubscribedSelector from '~/redux/hooks/useIsSubscribedSelector';
import { previousPathSelector } from '~/redux/slices/location/selectors';
import {
  CARDS_PATH,
  EARN_COINS_PATH,
  EARN_DIAMONDS_PATH,
  HOME_PATH,
  SEASON_TICKET_MODAL_PATH,
  SEASON_TICKET_PATH,
} from '~/routes/paths';
import InventoryIcon from '~/views/CardListView/InventoryIcon';

import lootcoinImage from '~/assets/images/currencies/coin_balance.png';
import lootgemImage from '~/assets/images/currencies/diamond_balance.png';
import ticketImage from '~/assets/images/currencies/ticket_balance.png';

import Balance from './Balance';

import styles from './Balances.module.scss';

interface IBalances {
  hide: boolean;
  backgroundTransparent: boolean;
}

export default function Balances({
  hide,
  backgroundTransparent,
}: IBalances): JSX.Element {
  const { lootcoinBalance, lootgemBalance, ticketBalance, _id } =
    useSelector(userSelector);
  const { totalRecords, lastSuccessfulFetch } = useFetchSelector(
    fetchCardsIfNeeded,
    cardsSelector,
  );
  const { pathname } = useLocation();
  const isCardsPath = matchPath(pathname, CARDS_PATH);
  const previousPath = useSelector(previousPathSelector);

  return (
    <div
      className={classNames(
        styles.balances,
        hide && styles.hide,
        backgroundTransparent && styles.backgroundTransparent,
      )}
    >
      <Balance
        src={lootcoinImage}
        alt="Lootcoin"
        to={EARN_COINS_PATH}
        value={lootcoinBalance}
        abbreviate
      />
      <Balance
        src={lootgemImage}
        alt="Lootgem"
        to={EARN_DIAMONDS_PATH}
        value={lootgemBalance}
      />
      <Balance
        src={ticketImage}
        alt="Ticket"
        to={
          useIsSubscribedSelector()
            ? SEASON_TICKET_MODAL_PATH
            : SEASON_TICKET_PATH
        }
        value={ticketBalance}
      />
      {_id && (
        <Balance
          Icon={InventoryIcon}
          to={isCardsPath ? previousPath || HOME_PATH : CARDS_PATH}
          value={totalRecords}
          loading={!lastSuccessfulFetch}
          className={isCardsPath ? styles.cardsPath : ''}
        />
      )}
    </div>
  );
}
